import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	styled,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
} from "@mui/material";
import { api, types } from "api";
import { getComparator, Order, stableSort } from "helpers/tableComparator";
import React, { useEffect, useState } from "react";
import { RegisteredTableHead } from "./RegisteredTableHead";
import { RegisteredTableRow } from "./RegisteredTableRow";
import { RegisteredTableToolbar } from "./RegisteredTableTopbar";

const PREFIX = "RegisteredTable";

const classes = {
	root: `${PREFIX}-root`,
	paper: `${PREFIX}-paper`,
	table: `${PREFIX}-table`,
	hide: `${PREFIX}-hide`,
	button: `${PREFIX}-button`,
	errBtn: `${PREFIX}-errBtn`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		width: "100%",
	},
	[`& .${classes.paper}`]: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.table}`]: {
		minWidth: 750,
	},

	[`& .${classes.hide}`]: {
		display: "none",
	},

	[`& .${classes.button}`]: {
		float: "left",
		margin: "10",
	},

	[`& .${classes.errBtn}`]: {
		color: theme.palette.error.main,
	},
}));

export interface RegisteredTableProps {
	/**
	 * Additional toolbar buttons.
	 */
	children?: JSX.Element | JSX.Element[];
	users: types.User[];
	onDelete: (ids: string[]) => void;
	onEditClick?: (id: string) => void;
	title?: string;
}

export const RegisteredTable = ({
	children,
	users,
	onDelete,
	onEditClick,
	title = "Registered Users",
}: RegisteredTableProps): JSX.Element => {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof types.User>("firstName");
	const [selected, setSelected] = useState<string[]>([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [open, setOpen] = useState(false);
	const [msg, setMsg] = useState("");
	const [me, setMe] = useState<types.User>();

	useEffect(() => {
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}

				setMe(res.data);
			})
			.catch(() => {
				setMsg("Error getting user data");
				setOpen(true);
			});
	}, []);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof types.User,
	): void => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		if (event.target.checked) {
			const newSelecteds = users.map((n) => n.userId);
			setSelected(newSelecteds);
			return;
		}
		setSelected([]);
	};

	const onCheckboxClick = (
		event: React.MouseEvent<unknown>,
		userid: string,
	): void => {
		const selectedIndex = selected.indexOf(userid);
		let newSelected: string[] = [];

		if (selectedIndex === -1) {
			// If the row does not been checked
			newSelected = [...selected, userid];
		} else if (selectedIndex === 0) {
			// If the row's selected record is at the begin of the list
			newSelected = [...selected.slice(1)];
		} else if (selectedIndex === selected.length - 1) {
			// If the row's selected record is at the end of the list
			newSelected = [...selected.slice(0, -1)];
		} else if (selectedIndex > 0) {
			// Other case when the row is selected
			newSelected = [
				...selected.slice(0, selectedIndex),
				...selected.slice(selectedIndex + 1),
			];
		}

		setSelected(newSelected);
	};

	const handleChangePage = (event: unknown, newPage: number): void => {
		setPage(newPage);
	};

	const handleClose = (): void => {
		setOpen(false);
	};

	// Handle delete user, will delete all users in the delete list.
	const handleDelete = (): void => {
		onDelete(selected);
		setOpen(false);
		setSelected([]);
	};

	// Open the dialog for deleting multiple users
	const handleDeleteMultipleOpen = (): void => {
		setMsg(
			"Are you sure you want to delete the selected accounts? This action is irreversible.",
		);
		setOpen(true);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<Root className={classes.root}>
			<Paper className={classes.paper}>
				<RegisteredTableToolbar
					numSelected={selected.length}
					handleDelete={handleDeleteMultipleOpen}
					title={title}
				>
					{children}
				</RegisteredTableToolbar>

				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						aria-label="enhanced table"
					>
						<RegisteredTableHead
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={users.length}
						/>
						<TableBody>
							{stableSort(users, getComparator(order, orderBy))
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row, index) => (
									<RegisteredTableRow
										selected={selected}
										onCheckboxClick={onCheckboxClick}
										onEditClick={onEditClick}
										row={row}
										key={row.email}
										labelId={`enhanced-table-checkbox-${index}`}
										isMe={row.userId === me?.userId}
									/>
								))}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={users.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					Delete selected accounts
				</DialogTitle>

				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{msg}
					</DialogContentText>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleClose}>CANCEL</Button>
					<Button onClick={handleDelete} className={classes.errBtn} autoFocus>
						DELETE
					</Button>
				</DialogActions>
			</Dialog>
		</Root>
	);
};
